import axios from "axios";
import Cookies from "js-cookie";
import dayjs from "dayjs";

import {
  DEFAULT_ADVISOR_FEE,
  DEFAULT_BIRTH_YEAR,
  DEFAULT_INFLATION_RATE,
  DEFAULT_RETIREMENT_AGE,
} from "../../../../../utils/constant";

import { getPercentValue } from "../../../../../utils/helper/general";
import { isOrganizationLevel } from "../../../../../utils/helper/specialized";

export const addGoal = ({ email, proposalData }) =>
  axios.post(
    `${process.env.REACT_APP_API_GATEWAY}/user/valueMap/prospectObjective/${email}`,
    {
      advisorFee: getPercentValue(DEFAULT_ADVISOR_FEE),
      annuities: proposalData.annuities_isOn ? proposalData.annuities : 0,
      annuities_isOn: !!proposalData.annuities_isOn,
      birthYear: proposalData.birthYear,
      calculated: proposalData.calculated,
      contributions: proposalData.contributions,
      email,
      investmentAmount: proposalData.investmentAmount,
      incomeChartType: "contributions",
      investmentDuration: proposalData.investmentDuration,
      investmentObjective: proposalData.investmentObjective,
      organization: proposalData.organization,
      otherIncome: proposalData.otherIncome_isOn ? proposalData.otherIncome : 0,
      otherIncome_isOn: !!proposalData.otherIncome_isOn,
      productId: proposalData.productId,
      retirementAge: DEFAULT_RETIREMENT_AGE,
      riskTolerance: proposalData.riskTolerance,
      secondQuizPartSkipped: false,
      socialSecurity: proposalData.socialSecurity_isOn
        ? proposalData.socialSecurity
        : 0,
      socialSecurity_isOn: !!proposalData.socialSecurity_isOn,
      targetAmount: proposalData.targetAmount,
      withdrawalLevel: proposalData.withdrawalLevel,
      yearToStartWithdrawals: proposalData.yearToStartWithdrawals,
    },
    {
      headers: {
        Authorization: `Bearer ${Cookies.get(`jwt__${email}`)}`,
      },
    }
  );

export const getEtfByTicker = ({ benchmark_ticker, ticker }) =>
  axios
    .post(`${process.env.REACT_APP_FASTAPI}/level/etf-stats`, {
      ticker,
      benchmark_ticker,
    })
    .then(response => response.data.data);

export const getLiveAssessmentV3 = requestBody =>
  axios
    .post(
      `${process.env.REACT_APP_FASTAPI}/level/projection`,
      {
        objective: {
          advisorFee: getPercentValue(
            requestBody.advisorFee || DEFAULT_ADVISOR_FEE
          ),
          annuities: requestBody.annuities_isOn ? requestBody.annuities : 0,
          birthYear:
            requestBody.birthYear ||
            (requestBody.currentAge
              ? dayjs().format("YYYY") - requestBody.currentAge
              : DEFAULT_BIRTH_YEAR),
          contributions: requestBody.contributions,
          inflationRate: requestBody.viewWithInflation
            ? DEFAULT_INFLATION_RATE
            : undefined,
          investmentAmount: requestBody.investmentAmount,
          investmentDuration: requestBody.investmentDuration,
          investmentObjective: requestBody.investmentObjective,
          otherIncome: requestBody.otherIncome_isOn
            ? requestBody.otherIncome
            : 0,
          riskTolerance: requestBody.riskTolerance,
          socialSecurity: requestBody.socialSecurity_isOn
            ? requestBody.socialSecurity
            : 0,
          timestamp: requestBody.updatedAt ?? requestBody.timestamp,
          withdrawalLevel: requestBody.withdrawalLevel,
          yearToStartWithdrawals: requestBody.yearToStartWithdrawals,
        },
        financialModelId: requestBody.financialModelId,
        productId: requestBody.productId,
      },
      {
        withCredentials: true,
      }
    )
    .then(response => response.data);

export const getLiveSimulationV3 = requestBody =>
  axios
    .post(
      `${process.env.REACT_APP_FASTAPI}/level/simulations`,
      {
        objective: {
          advisorFee: getPercentValue(
            requestBody.advisorFee || DEFAULT_ADVISOR_FEE
          ),
          annuities: requestBody.annuities_isOn ? requestBody.annuities : 0,
          birthYear:
            requestBody.birthYear ??
            dayjs().format("YYYY") - requestBody.currentAge,
          contributions: requestBody.contributions,
          inflationRate: requestBody.viewWithInflation
            ? DEFAULT_INFLATION_RATE
            : undefined,
          investmentAmount: requestBody.investmentAmount,
          investmentDuration: requestBody.investmentDuration,
          investmentObjective: requestBody.investmentObjective,
          otherIncome: requestBody.otherIncome_isOn
            ? requestBody.otherIncome
            : 0,
          riskTolerance: requestBody.riskTolerance,
          socialSecurity: requestBody.socialSecurity_isOn
            ? requestBody.socialSecurity
            : 0,
          timestamp: requestBody.updatedAt ?? requestBody.timestamp,
          withdrawalLevel: requestBody.withdrawalLevel,
          yearToStartWithdrawals: requestBody.yearToStartWithdrawals,
        },
        productId: requestBody.productId,
        simulations: requestBody.simulations,
      },
      {
        withCredentials: true,
      }
    )
    .then(response => response.data);

export const getLiveStressorV3Chance = requestBody =>
  axios
    .post(
      `${process.env.REACT_APP_FASTAPI}/level/stressorChance`,
      {
        marketEvent: requestBody.marketEvent,
        objective: {
          advisorFee: getPercentValue(
            requestBody.advisorFee || DEFAULT_ADVISOR_FEE
          ),
          annuities: requestBody.annuities_isOn ? requestBody.annuities : 0,
          birthYear:
            requestBody.birthYear ??
            dayjs().format("YYYY") - requestBody.currentAge,
          contributions: requestBody.contributions,
          inflationRate: requestBody.viewWithInflation
            ? DEFAULT_INFLATION_RATE
            : undefined,
          investmentAmount: requestBody.investmentAmount,
          investmentDuration: requestBody.investmentDuration,
          investmentObjective: requestBody.investmentObjective,
          otherIncome: requestBody.otherIncome_isOn
            ? requestBody.otherIncome
            : 0,
          riskTolerance: requestBody.riskTolerance,
          socialSecurity: requestBody.socialSecurity_isOn
            ? requestBody.socialSecurity
            : 0,
          timestamp: requestBody.updatedAt ?? requestBody.timestamp,
          withdrawalLevel: requestBody.withdrawalLevel,
          yearToStartWithdrawals: requestBody.yearToStartWithdrawals,
        },
        productId: requestBody.productId,
      },
      {
        withCredentials: true,
      }
    )
    .then(response => response.data);

export const getLiveStressorV3Projection = requestBody =>
  axios
    .post(
      `${process.env.REACT_APP_FASTAPI}/level/stressorProjection`,
      {
        marketEvent: requestBody.marketEvent,
        objective: {
          advisorFee: getPercentValue(
            requestBody.advisorFee || DEFAULT_ADVISOR_FEE
          ),
          annuities: requestBody.annuities_isOn ? requestBody.annuities : 0,
          birthYear:
            requestBody.birthYear ??
            dayjs().format("YYYY") - requestBody.currentAge,
          contributions: requestBody.contributions,
          inflationRate: requestBody.viewWithInflation
            ? DEFAULT_INFLATION_RATE
            : undefined,
          investmentAmount: requestBody.investmentAmount,
          investmentDuration: requestBody.investmentDuration,
          investmentObjective: requestBody.investmentObjective,
          otherIncome: requestBody.otherIncome_isOn
            ? requestBody.otherIncome
            : 0,
          riskTolerance: requestBody.riskTolerance,
          socialSecurity: requestBody.socialSecurity_isOn
            ? requestBody.socialSecurity
            : 0,
          timestamp: requestBody.updatedAt ?? requestBody.timestamp,
          withdrawalLevel: requestBody.withdrawalLevel,
          yearToStartWithdrawals: requestBody.yearToStartWithdrawals,
        },
        productId: requestBody.productId,
      },
      {
        withCredentials: true,
      }
    )
    .then(response => response.data);

export const getMc = requestBody =>
  axios
    .post(
      `${process.env.REACT_APP_FASTAPI}/level/mc`,
      {
        objective: {
          advisorFee: getPercentValue(
            requestBody.advisorFee || DEFAULT_ADVISOR_FEE
          ),
          annuities: requestBody.annuities_isOn ? requestBody.annuities : 0,
          birthYear:
            requestBody.birthYear ??
            dayjs().format("YYYY") - requestBody.currentAge,
          contributions: requestBody.contributions,
          inflationRate: requestBody.viewWithInflation
            ? DEFAULT_INFLATION_RATE
            : undefined,
          investmentAmount: requestBody.investmentAmount,
          investmentDuration: requestBody.investmentDuration,
          investmentObjective: requestBody.investmentObjective,
          otherIncome: requestBody.otherIncome_isOn
            ? requestBody.otherIncome
            : 0,
          riskTolerance: requestBody.riskTolerance,
          socialSecurity: requestBody.socialSecurity_isOn
            ? requestBody.socialSecurity
            : 0,
          timestamp: requestBody.updatedAt ?? requestBody.timestamp,
          withdrawalLevel: requestBody.withdrawalLevel,
          yearToStartWithdrawals: requestBody.yearToStartWithdrawals,
        },
        productId: requestBody.productId,
      },
      {
        withCredentials: true,
      }
    )
    .then(response => response.data);

export const updateGoal = ({ email, goalId, proposalData }) =>
  axios.put(
    `${process.env.REACT_APP_API_GATEWAY}/user/valueMap/prospectObjective/${goalId}/${email}`,
    {
      advisorFee: getPercentValue(DEFAULT_ADVISOR_FEE),
      annuities: proposalData.annuities_isOn ? proposalData.annuities : 0,
      annuities_isOn: !!proposalData.annuities_isOn,
      birthYear: proposalData.birthYear,
      calculated: proposalData.calculated,
      contributions: proposalData.contributions,
      email,
      investmentAmount: proposalData.investmentAmount,
      incomeChartType: "contributions",
      investmentDuration: proposalData.investmentDuration,
      investmentObjective: proposalData.investmentObjective,
      organization: proposalData.organization,
      otherIncome: proposalData.otherIncome_isOn ? proposalData.otherIncome : 0,
      otherIncome_isOn: !!proposalData.otherIncome_isOn,
      productId: proposalData.productId,
      retirementAge: DEFAULT_RETIREMENT_AGE,
      riskTolerance: proposalData.riskTolerance,
      secondQuizPartSkipped: false,
      socialSecurity: proposalData.socialSecurity_isOn
        ? proposalData.socialSecurity
        : 0,
      socialSecurity_isOn: !!proposalData.socialSecurity_isOn,
      targetAmount: proposalData.targetAmount,
      withdrawalLevel: proposalData.withdrawalLevel,
      yearToStartWithdrawals: proposalData.yearToStartWithdrawals,
    },
    {
      headers: {
        Authorization: `Bearer ${Cookies.get(`jwt__${email}`)}`,
      },
    }
  );
